import {
  Accordion,
  AccordionContext,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  useAccordionButton,
} from "react-bootstrap";
import TrowelBoxWrapper from "../../components/boxes/wrapper";
import Barchart from "../../components/gc/bar-chart";
import SentimentGuage from "../../components/gc/sentiment-guage";
import "./index.scss";
import { ReactNode, useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IUserSessionInfo } from "../../redux/reducers/user-session/userSessionSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";
import axios from "axios";
import CompanyInsightsAccordion from "../../components/company-insights";
import trowelIcon from "../../assets/media/icons/trowelLogoNew.png";
import spinnerAnim from "../../assets/media/icons/spinner.gif";
import chatMessageSendIcon from "../../assets/media/icons/chatMessageSendIcon.png";
import copyIcon from "../../assets/media/icons/copy-Icon.png";
import thumbsUpIcon from "../../assets/media/icons/thumbs-up-icon.png";
import thumbsDownIcon from "../../assets/media/icons/thumbs-down-icon.png";
import ReactMarkdown from 'react-markdown';
import { useChatEnhanced } from "../../hooks/useChatEnhanced";
import { WidgetRenderer } from "../../components/widgets/WidgetRenderer";

interface ContextAwareToggleProps {
  children: ReactNode;
  eventKey: string;
  callback?: (eventKey: string) => void;
}

function ContextAwareToggle({
  children,
  eventKey,
  callback,
}: ContextAwareToggleProps) {
  const { activeEventKey } = useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => callback && callback(eventKey)
  );

  const isCurrentEventKey = activeEventKey === eventKey;
  return (
    <div className="trwl-custom-accordion-header" onClick={decoratedOnClick}>
      {children}
    </div>
  );
}

interface ConversationMessage {
  messageId: string;
  conversationId: string;
  sender: 'assistant' | 'user';
  text: string;
  payload: {
    type: string;
    insight?: InsightPayload;
    execution_id?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface InsightPayload {
  executionId: string;
  conversationId: string;
  createdAt: string;
  firmId: string;
  status: string;
  updatedAt: string;
  insightId: string;
  insights: Array<{
    pain_area_id: string;
    pain_area: string;
    impact_level: string;
    why_it_matters: string;
    related_goal_ids: string[];
    root_cause: Array<{
      root_cause: string;
      supporting_facts: string[];
    }>;
    categories: Array<{
      category: string;
      solution_description: string;
      recommended_actions: Array<{
        description: string;
        why_it_helps: string;
      }>;
      confidence: string;
      evidences: Array<{
        source: string;
        statement: string;
      }>;
      immediate_step: string;
    }>;
  }>;
}

class FirmConversation {
  title: string;
  description: string;
  isDefault: boolean;
  conversationId: string;
  firmId: string;
  isActive: boolean;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  messages: ConversationMessage[];

  constructor(data: any) {
    this.title = data.title;
    this.description = data.description;
    this.isDefault = data.isDefault;
    this.conversationId = data.conversationId;
    this.firmId = data.firmId;
    this.isActive = data.isActive;
    this.messageCount = data.messageCount;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.messages = data.messages || [];
  }

  getInsightMessage(): ConversationMessage | null {
    return this.messages.find(msg => 
      msg.payload?.type === 'insight' && msg.payload?.insight
    ) || null;
  }

  getRegularMessages(): ConversationMessage[] {
    return this.messages.filter(msg => 
      !msg.payload || msg.payload.type !== 'insight'
    );
  }
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  let apiUrlBase = process.env.REACT_APP_API_URL || "https://trowelwa01.azurewebsites.net/api/";
  if (!apiUrlBase.endsWith('/')) {
    apiUrlBase += '/';
  }
  console.log("React api url ", process.env.REACT_APP_API_URL);
  const [show, setShow] = useState<boolean>(false);
  const [firmConversation, setFirmConversation] =
    useState<FirmConversation | null>(null);
  const [selectedInsight, setSelectedInsight] = useState<any | null>(null);
  const [likedInsights, setLikedInsights] = useState<{ [id: string]: boolean }>(
    {}
  );
  const [showInsightPanel, setShowInsightPanel] = useState<boolean>(false);
  const [conversationRendered, setConversationRendered] = useState<boolean>(false);
  const [apiMessages, setApiMessages] = useState<any[]>([]);
  const chatSectionRef = useRef<HTMLDivElement>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use the enhanced chat hook
  const { messages: chatMessages, statusMessage, isProcessing, sendMessage: sendChatMessage } = useChatEnhanced();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    if (chatSectionRef.current) {
      chatSectionRef.current.scrollTop = chatSectionRef.current.scrollHeight;
    }
  }, [chatMessages, statusMessage]);

  const insightExecutionId = useSelector(
    (state: RootState) => state.commanyInsightReducer.executionId
  );

  const userSessionFirmId = useSelector(
    (state: RootState) => state.userSessionStateReducer.firmId
  );

  const userSessionInfo = useSelector(
    (state: RootState) => state.userSessionStateReducer
  );

  const HeartIcon = ({ filled }: { filled: boolean }) =>
    filled ? (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="#e25555"
        stroke="#e25555"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 21C12 21 4 13.36 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.08C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 13.36 16 21 16 21H12Z" />
      </svg>
    ) : (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#e25555"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 21C12 21 4 13.36 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.08C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 13.36 16 21 16 21H12Z" />
      </svg>
    );

  const toggleLike = (insightId: string) => {
    setLikedInsights((prev) => ({
      ...prev,
      [insightId]: !prev[insightId],
    }));
  };

  const handleShowInsights = () => {
    setShowInsightPanel(true);
  };

  const handleCloseInsights = () => {
    setShowInsightPanel(false);
    setSelectedInsight(null);
  };

  const handleWidgetAction = (actionType: string, parameters: any) => {
    console.log('Widget action:', actionType, parameters);
    
    if (actionType === 'view_detail') {
      // Get the item from the parameters
      const itemId = parameters.item_id;
      
      // Find the message with list widget to get the item details
      const message = chatMessages.find(msg => 
        msg.contentSegments?.some(seg => 
          seg.type === 'widget' && 
          seg.widget?.widget_type === 'list'
        )
      );
      
      if (message) {
        const listSegment = message.contentSegments?.find(seg => 
          seg.type === 'widget' && seg.widget?.widget_type === 'list'
        );
        
        if (listSegment && listSegment.type === 'widget' && listSegment.widget?.spec?.items) {
          const selectedItem = listSegment.widget.spec.items[itemId];
          setSelectedInsight(selectedItem);
          setShowInsightPanel(true);
        }
      }
    }
  };

  const handleSendMessage = async () => {
    const chatInput = document.getElementById('chatInput') as HTMLTextAreaElement;
    const message = chatInput?.value.trim();
    
    if (!message || isProcessing) {
      return;
    }

    // Clear the input immediately
    if (chatInput) {
      chatInput.value = '';
    }

    // Send message using the enhanced chat hook
    await sendChatMessage(message);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const readConversationData = () => {
    // Clear any existing timeout before making new request
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    
    console.log("Initiating conversation data polling...");
    fetchConversationData((data) => {
      console.log("Conversation data fetched:", data);
      
      // Always update messages, regardless of isActive status
      updateFirmConversation(data);
      
      if (data && data.isActive === true) {
        console.log("Conversation is active (insights completed), stopping polling.");
        return;
      }
      
      // Continue polling 10 seconds after receiving the response
      pollingTimeoutRef.current = setTimeout(() => {
        readConversationData();
      }, 30000);
    });
  };

  const updateFirmConversation = (data: any) => {
    const conversation = new FirmConversation(data);
    setFirmConversation(conversation);
    
    // Check if messages have changed before re-rendering
    const currentMessageIds = apiMessages.map(m => m.messageId).join(',');
    const newMessageIds = conversation.messages.map(m => m.messageId).join(',');
    
    if (currentMessageIds === newMessageIds && conversationRendered) {
      console.log("No new messages, skipping re-render");
      return;
    }
    
    // Convert conversation messages to display format
    const convertedMessages: any[] = [];
    
    conversation.messages.forEach((msg) => {
      if (msg.payload?.type === 'insight' && msg.payload?.insight) {
        // Create insight widget message
        const insightData = msg.payload.insight;
        const insightItems = insightData.insights.map((insight: any, idx: number) => ({
          id: insight.pain_area_id,
          title: insight.pain_area,
          description: insight.why_it_matters,
          impact: insight.impact_level,
          categories: insight.categories
        }));
        
        convertedMessages.push({
          id: msg.messageId,
          type: 'assistant',
          content: msg.text,
          contentSegments: [
            {
              type: 'text',
              content: msg.text
            },
            {
              type: 'widget',
              widget: {
                widget_id: `insight-${msg.messageId}`,
                widget_type: 'list',
                spec: {
                  title: 'Key Insights & Recommendations',
                  items: insightItems
                }
              }
            }
          ],
          createdAt: msg.createdAt,
          isStreaming: false
        });
      } else {
        // Regular message
        convertedMessages.push({
          id: msg.messageId,
          type: msg.sender,
          content: msg.text,
          contentSegments: [
            {
              type: 'text',
              content: msg.text
            }
          ],
          createdAt: msg.createdAt,
          isStreaming: false
        });
      }
    });
    
    setApiMessages(convertedMessages);
    setConversationRendered(true);
  };

  useEffect(() => {
    // Start polling on component mount
    readConversationData();

    // Cleanup: clear timeout on component unmount
    return () => {
      console.log("DashboardPage component unmounted, stopping conversation polling...");
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConversationData = (
    callbackfn: (data: any) => void = (data) => {}
  ) => {
    if (!userSessionFirmId) {
      console.error("Error: firmId is undefined. Cannot fetch conversation data.");
      console.log("User session info:", userSessionInfo);
      return;
    }

    let conversationUrl = `${apiUrlBase}v2/firms/${userSessionFirmId}/conversations/default`;

    axios
      .get(conversationUrl, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      })
      .then((response) => {
        console.log("Conversation data fetched successfully:", response.data);
        callbackfn(response.data);
      })
      .catch((error) => {
        console.error("Error fetching conversation data:", error);
      });
  };

  return (
    <main className="db-main">
      <div className="mobile-tabs d-lg-none">
        <button className="active" id="tabChat">
          Chat
        </button>
        <button id="tabInsights">Insights</button>
      </div>

      <div className="container-fluid no-pd-mg">
        <div ref={chatSectionRef} className={`chat-section active pd-t-xs ${!showInsightPanel ? 'chat-centered' : ''}`} id="chatView">
          <div className="chat-container">
            {/* API Messages from v2 conversation */}
            {apiMessages.map((msg) => (
              <div key={msg.id} className="chat-messages">
                <div className="message-wrapper">
                  {msg.type === 'user' ? (
                    <div className="request-container">{msg.content}</div>
                  ) : (
                    <div className="response-container">
                      <div className="icon-header">
                        <img width="60" height="60" src={trowelIcon} alt="Trowel AI" />
                      </div>
                      <div className="chat-response">
                        {msg.contentSegments && msg.contentSegments.length > 0 ? (
                          msg.contentSegments.map((segment: any, idx: number) => (
                            segment.type === 'text' ? (
                              <div key={idx} className="markdown-content">
                                <ReactMarkdown>{segment.content}</ReactMarkdown>
                              </div>
                            ) : segment.type === 'widget' ? (
                              <div key={idx} className="widget-container">
                                <WidgetRenderer widget={segment.widget} onAction={handleWidgetAction} />
                              </div>
                            ) : null
                          ))
                        ) : msg.content ? (
                          <div className="markdown-content">
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>
                        ) : null}
                      </div>
                      {!msg.isStreaming && (msg.contentSegments?.length || msg.content) && (
                        <div className="chats-actions-bottom">
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <img src={copyIcon} alt="Copy" width="14" height="14" className="me-2" />
                          </button>
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <img src={thumbsUpIcon} alt="Thumbs Up" width="14" height="14" className="me-2" />
                          </button>
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <img src={thumbsDownIcon} alt="Thumbs Down" width="14" height="14" className="me-2" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Old hardcoded welcome message - can be removed if not needed */}
            {apiMessages.length === 0 && (
              <div className="chat-messages">
                <div className="message-wrapper">
                  <div className="response-container">
                    <div className="icon-header">
                      <img width="60" height="60" src={trowelIcon} alt="Trowel AI" />
                    </div>
                    <div className="chat-response">
                      <p>
                       👋 Hey  <b style={{fontSize: "1.3rem"}}>{userSessionInfo?.name || "User"}</b> ! Welcome aboard!
                  <br />
                  <br />
                  We’re thrilled to have you on this journey to grow,
                  streamline, and transform your business.
                  <br />
                  <br />
                  Your smart assistant is here to uncover valuable insights,
                  deliver practical recommendations, and support your growth
                  every step of the way.
                  <br />
                  <br />
                  <strong>
                    Let’s explore what’s next! What would you like help with
                    first?
                  </strong>
                </p>
              </div>
              <div className="chats-actions-bottom">
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <img src={copyIcon} alt="Copy" width="14" height="14" className="me-2" />
                </button>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <img src={thumbsUpIcon} alt="Thumbs Up" width="14" height="14" className="me-2" />
                </button>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <img src={thumbsDownIcon} alt="Thumbs Down" width="14" height="14" className="me-2" />
                </button>
              </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dynamic chat messages from user interactions */}
            {chatMessages.map((msg) => (
              <div key={msg.id} className="chat-messages">
                <div className="message-wrapper">
                  {msg.type === 'user' ? (
                    <div className="request-container">{msg.content}</div>
                  ) : (
                    <div className="response-container">
                      <div className="icon-header">
                        <img width="60" height="60" src={trowelIcon} alt="Trowel AI" />
                      </div>
                      <div className="chat-response">
                        {/* Render content segments in order */}
                        {msg.contentSegments && msg.contentSegments.length > 0 ? (
                          msg.contentSegments.map((segment, idx) => (
                            segment.type === 'text' ? (
                              <div key={idx} className="markdown-content">
                                <ReactMarkdown>{segment.content}</ReactMarkdown>
                              </div>
                            ) : segment.type === 'widget' ? (
                              <div key={idx} className="widget-container">
                                <WidgetRenderer widget={segment.widget} onAction={handleWidgetAction} />
                              </div>
                            ) : null
                          ))
                        ) : msg.content ? (
                          // Fallback for user messages or legacy messages
                          <div className="markdown-content">
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>
                        ) : null}
                        
                        {/* Show streaming status below content */}
                        {msg.isStreaming && msg.streamingStatus && (
                          <div className="processing-stats">
                            <img src={spinnerAnim} width="24" height="25" alt="Loading" />
                            <span>{msg.streamingStatus}</span>
                          </div>
                        )}
                        
                        {/* Show generic thinking message if streaming but no status yet */}
                        {msg.isStreaming && !msg.streamingStatus && (!msg.contentSegments || msg.contentSegments.length === 0) && (
                          <div className="processing-stats">
                            <img src={spinnerAnim} width="24" height="25" alt="Loading" />
                            <span>Thinking...</span>
                          </div>
                        )}
                      </div>
                      {!msg.isStreaming && (msg.contentSegments?.length || msg.content) && (
                        <div className="chats-actions-bottom">
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <img src={copyIcon} alt="Copy" width="14" height="14" className="me-2" />
                          </button>
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <img src={thumbsUpIcon} alt="Thumbs Up" width="14" height="14" className="me-2" />
                          </button>
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <img src={thumbsDownIcon} alt="Thumbs Down" width="14" height="14" className="me-2" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

          <div className="chat-messages" style={{ display: 'none' }}>
            <div className="message-wrapper">
              <div className="request-container">Hi Trowel</div>
            </div>
          </div>

          <div className="chat-messages" style={{ display: 'none' }}>
            <div className="message-wrapper">
              <div className="response-container">
                <div className="icon-header">
                  <img width="60" height="60" src={trowelIcon}/>
              </div>
              <div className="chat-response">
                <p>
                  We’ve pinpointed 6 key challenges and recommendations spread
                  across areas like Sales, Marketing, HR, and Technology.
                </p>
                <div className="processing-stats">
                  <img src={spinnerAnim} width="24" height="25" />
                  <span>Thinking...</span>
                </div>
                <div className="recommendation-list">
                  <a href="#" onClick={(e) => { e.preventDefault(); handleShowInsights(); }}>
                    <div className="recommendation-item">
                      <div className="highlight-box high">
                        Maintaining long-term relationships with employers and
                        securing repeat business.
                      </div>
                    </div>
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleShowInsights(); }}>
                    <div className="recommendation-item">
                      <div className="highlight-box medium">
                        Maintaining long-term relationships with employers and
                        securing repeat business.
                      </div>
                    </div>
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleShowInsights(); }}>
                    <div className="recommendation-item">
                      <div className="highlight-box low">
                        Maintaining long-term relationships with employers and
                        securing repeat business.
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="chats-actions-bottom">
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <img src={copyIcon} alt="Copy" width="14" height="14" className="me-2" />
                </button>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <img src={thumbsUpIcon} alt="Thumbs Up" width="14" height="14" className="me-2" />
                </button>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <img src={thumbsDownIcon} alt="Thumbs Down" width="14" height="14" className="me-2" />
                </button>
              </div>
            </div>
            </div>
            </div>
          </div>
        </div>

        {showInsightPanel && (
          <div className="insight-section pd-t-xs" id="insightView">
            <button 
              className="insight-close-btn" 
              onClick={handleCloseInsights}
              aria-label="Close insights panel"
            >
              X
            </button>
            <div className="card-insight">
              <h2>
                <div className="indicator"></div>
                {selectedInsight?.title || 'Keeping up with trends like AI-powered job matching chatbots.'}
              </h2>
            <div className="tag">Insight</div>

            <div className="solution">
              <p>
                <strong>Details:</strong> {selectedInsight?.description || 'Understand how AI-powered job matching and chatbots can benefit your platform and identify industry standards.'}
              </p>
            </div>

            {/* <div className="form-check1">
              <input
                className="form-check-input"
                type="checkbox"
                value=""
                id="checkDefault"
              />
              <label className="form-check-label" htmlFor="checkDefault">
                Default checkbox
              </label>
            </div> */}

            {/* <div className="icons">
              <div className="chats-actions-bottom">
                <a href="#">
                  <i className="bi bi-copy me-2"></i>
                </a>
                <a href="#">
                  <i className="bi bi-hand-thumbs-up me-2"></i>
                </a>
                <a href="#">
                  <i className="bi bi-hand-thumbs-down me-2"></i>
                </a>
              </div>
            </div> */}

            <button 
              onClick={() => navigate('/designer-studio')} 
              className="cta"
              style={{ cursor: 'pointer', textAlign: 'left' }}
            >
              ✨ Create an Advertisement
            </button>

            <div className="stats">
              <div className="stat-card stat-sales">
                <div className="stat-value">+15%</div>
                <div className="stat-tag">Sales</div>
                <p>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit
                  lacusscipit.
                </p>
              </div>
              <div className="stat-card stat-tech">
                <div className="stat-value">+10%</div>
                <div className="stat-tag">Technology</div>
                <p>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit lacus.
                </p>
              </div>
            </div>

            <div className="sources">
              <strong>Sources</strong>
              <ul>
                <li>
                  Donec non orci at ipsum convallis placerat eu nec lectus.
                </li>
                <li>Sed luctus tristique suscipit nisi elementum in.</li>
              </ul>
            </div>
          </div>
        </div>
        )}
      </div>

      <div className={`chat-input-area ${!showInsightPanel ? 'chat-input-centered' : ''}`}>
        <textarea 
          id="chatInput" 
          placeholder="Ask anything..."
          onKeyDown={handleKeyDown}
        ></textarea>
        <button id="sendBtn" onClick={handleSendMessage}>
          <img src={chatMessageSendIcon} alt="Send" className="send-icon" />
        </button>
      </div>
    </main>
  );
};

export default DashboardPage;

    // <main className="db-main">
    //   <div className="db-main__welcome-text">
    //     <span>Welcome </span>
    //   </div>
    //   <Container fluid className="gx-0 trwl-container">
    //     <Row>
    //       <Col xs={12} md={6} lg={6}>
    //         {companyInsights ? (
    //          <CompanyInsightsAccordion
    //          insights={companyInsights.insights.insights}
    //          likedInsights={likedInsights}
    //          onToggleLike={toggleLike}
    //          onViewMore={insight => {
    //            setSelectedInsight(insight);
    //            handleShow();
    //          }}
    //          ContextAwareToggle={ContextAwareToggle}
    //          HeartIcon={HeartIcon}
    //        />
    //         ) : (
    //           <div className="trwl-insights-loading">
    //             <div className="insights-loading-content">
    //               <div className="insights-spinner"></div>
    //               <div className="insights-loading-text">
    //                 Generating insights data...
    //               </div>
    //               <div className="insights-loading-subtext">
    //                 Please wait a moment to see your results.
    //               </div>
    //             </div>
    //           </div>
    //         )}
    //       </Col>
    //       <Col xs={12} md={6} lg={6} className="stat-box-right">
    //         <Container fluid className="trwl-chat-container gy-3">
    //           <Card className="trwl-chat-box">
    //             <Container fluid className="chat-container">
    //               <Row className="header text-center py-2">
    //                 <Col>
    //                   <div className="trwl-chat-header">
    //                     <div className="logo">
    //                       <span className="trwl-logo__text">
    //                         TROWEL.AI Connect
    //                       </span>
    //                     </div>
    //                     <div className="chat-box-icons">
    //                       <span className="container-icon chat-box-icons__icon"></span>
    //                       <span className="container-align-icon chat-box-icons__icon"></span>
    //                     </div>
    //                   </div>
    //                 </Col>
    //               </Row>
    //               <Row className="chat-content flex-grow-1 overflow-auto">
    //                 <Col className="py-2 chat-body">
    //                   <div className="chat-message-wrapper">
    //                     <span className="trwl-logo__icon"></span>
    //                     <div className="chat-message">
    //                       <div className="message-info">
    //                         Hi {userSessionInfo?.name || "User"}, Here's a quick snapshot of what we've found
    //                         for you:
    //                       </div>
    //                       <div className="message-info">
    //                         We’ve pinpointed 6 key challenges spread across
    //                         areas like Sales, Marketing, HR, and Technology.
    //                       </div>
    //                       <div className="message-info">
    //                         There are 8 personalized recommendations ready to
    //                         tackle these challenges and drive results.
    //                       </div>
    //                     </div>
    //                   </div>
    //                 </Col>
    //               </Row>
    //               <Row className="footer py-2">
    //                 <Col>
    //                   <div className="user-message-input">
    //                     <div className="input-box">
    //                       <input type="text" placeholder="Message" />
    //                     </div>

    //                     <div className="user-message-toolbox">
    //                       <div className="attach-file"></div>
    //                       <div className="chat-message-send"></div>
    //                     </div>
    //                   </div>
    //                 </Col>
    //               </Row>
    //             </Container>
    //           </Card>
    //         </Container>
    //       </Col>
    //     </Row>
    //   </Container>

    //   <Modal
    //     show={show}
    //     onHide={handleClose}
    //     centered
    //     backdrop="static"
    //     size="lg"
    //     dialogClassName="trwl-model-dialog"
    //     keyboard={false}
    //   >
    //     <Modal.Header closeButton className="model-dialog-header">
    //       <span className="model-dialog-header__head-text">
    //         {selectedInsight ? selectedInsight.title : "Insight Details"}
    //       </span>
    //     </Modal.Header>
    //     <Modal.Body className="model-dialog-body">
    //       <Container fluid>
    //         <Row>
    //           <Col xs={12} sm={12} md={8} lg={8} className="left-portion">
    //             {selectedInsight ? (
    //               <>
    //                 <div className="solution-text">Solution</div>
    //                 <div>{selectedInsight.description}</div>
    //                 {selectedInsight.sources && selectedInsight.sources.length > 0 && (
    //                   <div className="insight-sources">
    //                     <div style={{ fontWeight: "bold", marginTop: "1rem" }}>Sources:</div>
    //                     <ul>
    //                       {selectedInsight.sources.map((src: any, srcIdx: number) => (
    //                         <li key={srcIdx}>
    //                           <span style={{ fontWeight: "bold" }}>{src.type}:</span> {src.highlight}
    //                         </li>
    //                       ))}
    //                     </ul>
    //                   </div>
    //                 )}
    //               </>
    //             ) : (
    //               <div>No insight selected.</div>
    //             )}
    //           </Col>
    //           <Col xs={12} sm={12} md={4} lg={4} className="right-portion">
    //             {/* You can add more dynamic content here if needed */}
    //           </Col>
    //         </Row>
    //       </Container>
    //     </Modal.Body>
    //     <Modal.Footer className="model-dialog-footer"></Modal.Footer>
    //   </Modal>
    // </main>